import React, { useEffect, useState } from 'react';
import { client, consoleLogInDev } from 'shared';
import { Modal, PillButton, Row, Stack, Text } from 'library/ui';
import { IBusinessLocation, PagingResult } from 'models';
import SelectLocations from './SelectLocations';
import SelectPermissions from './SelectPermissions';
import { IPermission, Role } from './models';
import SelectRoles from './SelectRoles';

interface IEditUserProps {
    name: string;
    userId?: number;
    initialLocations?: string[];
    initialPermissions?: string[];
    initialRoles?: string[];
    isModalOpen: boolean;
    onClose: () => void;
    onUserUpdated: () => void;
}

export default function EditUser({
    name,
    userId,
    initialLocations,
    initialPermissions,
    initialRoles,
    isModalOpen,
    onClose,
    onUserUpdated,
}: IEditUserProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
    const [availableLocations, setAvailableLocations] = useState<IBusinessLocation[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<IPermission[]>([]);
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

    useEffect(() => {
        if (!isModalOpen || (availableLocations.length && availablePermissions.length)) {
            return;
        }

        client(`api/business-location/?page_size=100`)
            .then((response: PagingResult<IBusinessLocation>) => {
                setAvailableLocations(response.results);
                setSelectedLocations(
                    response.results
                        .filter((x) => initialLocations?.includes(x.name) || initialLocations?.length === 0)
                        .map((x) => x.id),
                );
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
        client(`api/permissions/?page_size=50`)
            .then((response: PagingResult<IPermission>) => {
                setAvailablePermissions(response.results);
                setSelectedPermissions(
                    response.results.filter((x) => initialPermissions?.includes(x.name)).map((x) => x.id),
                );
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
        client(`api/role/?type=BUSINESS_CONTACT&page_size=50`)
            .then((response: PagingResult<Role>) => {
                setAvailableRoles(response.results);
                setSelectedRoles(
                    response.results.filter((x) => initialRoles?.includes(x.display_name)).map((x) => x.id),
                );
            })
            .catch((error) => {
                consoleLogInDev(error);
            });
    }, [isModalOpen]);

    useEffect(() => {
        if (availableLocations.length) {
            if (initialLocations?.length === 0) {
                setSelectedLocations(availableLocations.map((x) => x.id));
            } else {
                setSelectedLocations(
                    availableLocations.filter((x) => initialLocations?.includes(x.name)).map((x) => x.id),
                );
            }
        }

        if (availablePermissions.length) {
            setSelectedPermissions(
                availablePermissions.filter((x) => initialPermissions?.includes(x.name)).map((x) => x.id),
            );
        }

        if (availableRoles.length) {
            setSelectedRoles(availableRoles.filter((x) => initialRoles?.includes(x.display_name)).map((x) => x.id));
        }
    }, [initialLocations, initialPermissions, initialRoles]);

    function onSubmitClick() {
        setErrorMessage('');
        setIsSubmitting(true);
        client(`api/business-contact/update-contact/`, {
            body: {
                user_id: userId,
                business_locations: selectedLocations.length !== availableLocations.length ? selectedLocations : [],
                permissions: selectedPermissions,
                roles: selectedRoles,
            },
        })
            .then(() => {
                onUserUpdated();
            })
            .catch(() => {
                setErrorMessage('An error occurred while updating the contact.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }

    function onLocationSelect(id: number) {
        if (selectedLocations.includes(id)) {
            setSelectedLocations(selectedLocations.filter((x) => x !== id));
        } else {
            setSelectedLocations([...selectedLocations, id]);
        }
    }

    function onPermissionSelect(id: number) {
        if (selectedPermissions.includes(id)) {
            setSelectedPermissions(selectedPermissions.filter((x) => x !== id));
        } else {
            setSelectedPermissions([...selectedPermissions, id]);
        }
    }

    function onLocationSelectAll() {
        if (!selectedLocations.length || selectedLocations.length < availableLocations.length) {
            setSelectedLocations(availableLocations.map((x) => x.id));
        } else {
            setSelectedLocations([]);
        }
    }

    function onRoleSelect(id: number) {
        if (selectedRoles.includes(id)) {
            setSelectedRoles(selectedRoles.filter((x) => x !== id));
        } else {
            setSelectedRoles([...selectedRoles, id]);
        }
    }

    function isSubmitDisabled() {
        return !selectedLocations.length || isSubmitting;
    }

    function closeModal() {
        setSelectedLocations([]);
        setSelectedPermissions([]);
        onClose();
    }

    if (!isModalOpen) return null;

    return (
        <Modal title={`Edit ${name}'s Account`} onClose={closeModal} width={700}>
            <Stack gap="md">
                <Text weight="semibold">Account Information</Text>
                <SelectLocations
                    availableLocations={availableLocations}
                    selectedLocations={selectedLocations}
                    onLocationSelect={onLocationSelect}
                    onSelectAll={onLocationSelectAll}
                />
                <SelectPermissions
                    availablePermissions={availablePermissions}
                    selectedPermissions={selectedPermissions}
                    onPermissionSelect={onPermissionSelect}
                />
                <SelectRoles
                    selectedRoles={selectedRoles}
                    onRoleSelect={onRoleSelect}
                    availableRoles={availableRoles}
                />
                <Row justify="end" gap="sm">
                    <PillButton variant="secondary" onClick={closeModal}>
                        Cancel
                    </PillButton>
                    <PillButton disabled={isSubmitDisabled()} onClick={onSubmitClick}>
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </PillButton>
                </Row>
                {errorMessage && <Text tone="error">{errorMessage}</Text>}
            </Stack>
        </Modal>
    );
}
